import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FloatingBallContainer,
  CloseButton,
  CloseIcon,
  BirdImage,
} from './styles';
import { ChatConversa } from '../chat/chat';
import { getIAResposta, getConversationId } from '../../apis/ia';
import { Message } from '../chat/types';
import { useSharedChat } from '../chat/hooks/useSharedChat';
import { MESSAGE_TYPES } from '../../modules/chat/constants';
import { handleAudioResponse, createLiaMessage, createErrorMessage } from '../../modules/chat/utils';
import './styles.css';
import { useWebgate } from '@xvia/webgate-connect';

const FloatingBall: React.FC = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { messages, setMessages, input, setInput } = useSharedChat();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const { userInfo } = useWebgate();
  const userInfoInjected = useRef(false);

  let validConversationId: boolean = false;
  let conversationId: string | null | undefined = null;

  const processUserMessage = useCallback(async (message: Message) => {
      if (!conversationId && !validConversationId) {
      conversationId = await getConversationId();

      validConversationId = true;
    }


    if (message.from === MESSAGE_TYPES.USER) {
      setLoading(true);
    }

    let messageText = typeof message.text === 'string' ? message.text : '';

    try {
      setMessages(prev => [...prev, message]);

      if (userInfo && !userInfoInjected.current) {
        messageText += ` userInfo: ${JSON.stringify(userInfo)}`;
        userInfoInjected.current = true;
      } else if (!userInfo && userInfoInjected.current) {
        userInfoInjected.current = false;

        localStorage.removeItem('conversationId');
      }

      const response = await getIAResposta(messageText, conversationId);

      if (message.type === MESSAGE_TYPES.AUDIO) {
        handleAudioResponse(response.toString());
      }

      const liaMessage = createLiaMessage(response.toString());
      setMessages(prev => [...prev, liaMessage]);
    } catch (error) {
      console.error('Erro ao obter resposta da IA:', error);
      const errorMessage = createErrorMessage();
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  }, [setMessages, userInfo]);


  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsClosing(true);
            timeoutRef.current = setTimeout(() => {
              setIsExpanded(false);
              setIsVisible(false);
              setIsClosing(false);
            }, 300);
          } else {
            setIsVisible(true);
          }
        });
      },
      {
        threshold: 0.1
      }
    );

    const chatContainer = document.getElementById('chat-container');
    if (chatContainer) {
      observer.observe(chatContainer);
    }

    return () => {
      if (chatContainer) {
        observer.unobserve(chatContainer);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };
    
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleBallClick = () => {
    setIsExpanded(true);
  };

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsVisible(false);
  };

  const handleMinimizeClick = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsExpanded(false);
      setIsClosing(false);
    }, 300);
  };

  const handleCloseExpandedClick = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
    }, 300);
  };

  if (!isVisible) return null;

  if (isExpanded) {
    const chatStyle = {
      position: 'fixed' as const,
      bottom: isMobile ? '16px' : '20px',
      right: isMobile ? '16px' : '20px',
      width: isMobile ? 'calc(100vw - 32px)' : '401px',
      height: '431px',
      zIndex: 1000,
      animation: isClosing 
        ? 'chatCollapse 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards'
        : 'chatExpand 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      willChange: 'transform, opacity',
    };

    return (
      <ChatConversa
        loading={loading}
        showControls={true}
        onMinimize={handleMinimizeClick}
        onClose={handleCloseExpandedClick}
        onSendMessage={processUserMessage}
        messages={messages}
        input={input}
        setInput={setInput}
        isFloatingBall={true}
        style={chatStyle}
        customStyles={{
          header: {
            bgcolor: '#FDFDFD',
          },
          avatar: {
            bgcolor: '#cacaca',
          },
        }}
      />
    );
  }

  return (
    <FloatingBallContainer onClick={handleBallClick} isClosing={isClosing}>
      <BirdImage src={`${process.env.PUBLIC_URL}/images/ia.gif`} alt="Passarinho Lia" />
      <CloseButton onClick={handleCloseClick}>
        <CloseIcon />
      </CloseButton>
    </FloatingBallContainer>
  );
};

export default FloatingBall; 
